import { AnimatedSection } from "@/components/common/AnimatedSection"
import { LegalPageLayout } from "@/components/common/LegalPageLayout"
import { LEGAL_LINKS } from "@/data/legal"
import { useSEO } from "@/hooks/useSEO"
import { createFileRoute } from "@tanstack/react-router"

export const Route = createFileRoute("/privacy-policy")({
  component: PrivacyPolicyPage,
})

function PrivacyPolicyPage() {
  useSEO({
    title: "Privacy Policy",
    description: "Orchestrate's privacy policy and data protection practices.",
    canonical: "https://orchestrate.global/privacy-policy",
  })

  return (
    <LegalPageLayout links={LEGAL_LINKS}>
      <div className="mx-auto max-w-3xl px-6 lg:px-8">
        {" "}
        {/* ← centered & readable width */}
        <AnimatedSection delay={0.1}>
          <section className="border-border/50 border-b pb-16 lg:pb-20">
            <h1 className="font-display mb-6 text-4xl tracking-tight sm:text-5xl lg:text-6xl">
              Privacy Policy
            </h1>
            <p className="text-muted mb-10 text-xl">Last updated: February 27, 2026</p>
            <p className="text-ink/85 text-lg leading-relaxed">
              Orchestrate Inc. ("we," "us," or "our") is committed to protecting your privacy. This
              Privacy Policy explains how we collect, use, disclose, and safeguard your information
              when you use our payment processing platform and related services.
            </p>
          </section>
        </AnimatedSection>
        <div className="space-y-16 py-12 lg:space-y-20 lg:py-16">
          <AnimatedSection delay={0.2}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Information We Collect</h2>

              <div className="space-y-10">
                <div>
                  <h3 className="mb-4 text-2xl font-semibold tracking-tight">
                    Personal Information
                  </h3>
                  <ul className="text-ink/80 marker:text-muted list-outside list-disc space-y-3 pl-6 leading-relaxed">
                    <li>Name, email address, phone number</li>
                    <li>Billing and payment information</li>
                    <li>Business registration details</li>
                    <li>Identity verification documents</li>
                    <li>Communication preferences</li>
                  </ul>
                </div>

                <div>
                  <h3 className="mb-4 text-2xl font-semibold tracking-tight">Transaction Data</h3>
                  <ul className="text-ink/80 marker:text-muted list-outside list-disc space-y-3 pl-6 leading-relaxed">
                    <li>Payment processing information</li>
                    <li>Transaction amounts and timestamps</li>
                    <li>Merchant and customer identifiers</li>
                    <li>Payment method details</li>
                    <li>Settlement and reconciliation data</li>
                  </ul>
                </div>

                <div>
                  <h3 className="mb-4 text-2xl font-semibold tracking-tight">
                    Technical Information
                  </h3>
                  <ul className="text-ink/80 marker:text-muted list-outside list-disc space-y-3 pl-6 leading-relaxed">
                    <li>IP address and device information</li>
                    <li>Browser and application usage data</li>
                    <li>API access logs and authentication tokens</li>
                    <li>Cookies and similar tracking technologies</li>
                  </ul>
                </div>
              </div>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={0.3}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">
                How We Use Your Information
              </h2>
              <div className="text-ink/80 space-y-6 leading-relaxed">
                <p>We use your information to:</p>
                <ul className="marker:text-muted list-outside list-disc space-y-3 pl-6">
                  <li>Provide and maintain our payment processing services</li>
                  <li>Process transactions and manage settlements</li>
                  <li>Verify identities and prevent fraud</li>
                  <li>Comply with legal and regulatory requirements</li>
                  <li>Provide customer support and technical assistance</li>
                  <li>Improve our services and develop new features</li>
                  <li>Communicate with you about your account and our services</li>
                  <li>Analyze usage patterns to optimize performance</li>
                </ul>
              </div>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={0.4}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">
                Data Sharing and Disclosure
              </h2>
              <p className="text-ink/80 mb-6 leading-relaxed">
                We may share your information in the following circumstances:
              </p>
              <ul className="text-ink/80 marker:text-muted list-outside list-disc space-y-4 pl-6 leading-relaxed">
                <li>
                  <strong className="text-ink">With Financial Institutions:</strong> To process
                  payments and settlements
                </li>
                <li>
                  <strong className="text-ink">Regulatory Authorities:</strong> To comply with
                  applicable laws and regulations
                </li>
                <li>
                  <strong className="text-ink">Service Providers:</strong> With trusted third-party
                  vendors who assist in service delivery
                </li>
                <li>
                  <strong className="text-ink">Business Transfers:</strong> In connection with
                  mergers, acquisitions, or asset sales
                </li>
                <li>
                  <strong className="text-ink">Legal Requirements:</strong> When required by law,
                  court order, or government request
                </li>
              </ul>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={0.5}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Data Security</h2>
              <p className="text-ink/80 mb-6 leading-relaxed">
                We implement industry-standard security measures to protect your information:
              </p>
              <ul className="text-ink/80 marker:text-muted list-outside list-disc space-y-4 pl-6 leading-relaxed">
                <li>PCI DSS Level 1 certified infrastructure</li>
                <li>End-to-end encryption for sensitive data</li>
                <li>Regular security audits and penetration testing</li>
                <li>Strict access controls and authentication protocols</li>
                <li>Secure data centers with 24/7 monitoring</li>
              </ul>
            </section>
          </AnimatedSection>

          {/* Repeat similar pattern for remaining sections – keeping consistent spacing & typography */}
          <AnimatedSection delay={0.6}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Your Rights</h2>
              <p className="text-ink/80 mb-6 leading-relaxed">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="text-ink/80 marker:text-muted list-outside list-disc space-y-4 pl-6 leading-relaxed">
                <li>Access to your personal information</li>
                <li>Correction of inaccurate information</li>
                <li>Deletion of your personal information</li>
                <li>Restriction of processing</li>
                <li>Data portability</li>
                <li>Objection to processing</li>
              </ul>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={0.7}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">
                International Data Transfers
              </h2>
              <p className="text-ink/80 leading-relaxed">
                Your information may be transferred to and processed in countries other than your
                own. We ensure appropriate safeguards are in place for international data transfers,
                including standard contractual clauses and other legal mechanisms as required.
              </p>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={0.8}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Cookies and Tracking</h2>
              <p className="text-ink/80 leading-relaxed">
                We use cookies and similar technologies to enhance your experience and analyze usage
                patterns. You can control cookies through your browser settings, though this may
                affect some functionality.
              </p>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={0.9}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Children's Privacy</h2>
              <p className="text-ink/80 leading-relaxed">
                Our services are not intended for individuals under 18 years of age. We do not
                knowingly collect personal information from children. If we become aware that we
                have collected information from a child, we will take steps to delete it promptly.
              </p>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={1.0}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Changes to This Policy</h2>
              <p className="text-ink/80 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any
                material changes by posting the updated policy on our website and, where required,
                by sending you an email notification.
              </p>
            </section>
          </AnimatedSection>
        </div>
      </div>
    </LegalPageLayout>
  )
}
